import { useNavigate, useSearchParams } from "react-router";
import { SearchX } from "lucide-react";
import { DataUnavailable } from "@/design/DataUnavailable";
import { SearchControls } from "@/design/SearchControls";
import {
  parseSearchParams,
  type BusinessCard,
  type Suggestion,
} from "@/modules/discovery/surfaces";
import { useMe } from "~/auth/MeProvider";
import { BusinessCardTile } from "~/components/BusinessCardTile";
import { FavoritesLink } from "~/components/FavoritesLink";
import { PublicPage } from "~/components/SiteChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { call } from "~/lib/api";
import { useQuery } from "~/lib/useQuery";

/**
 * SearchScreen (docs/ux/screens/SearchScreen.md), public discovery.
 *
 * **The query string is still the single source of truth.** Every control
 * rewrites `/search?…` and the results follow; the only thing that changed with
 * the split is who re-runs the query. On the Next host the URL change was a
 * navigation and the server re-rendered; here the URL change is a navigation
 * and this effect re-fetches. Keeping the URL in charge is what makes a search
 * shareable, bookmarkable and survivable across a reload, and it is why the
 * filters are parsed with **Discovery's own parser** rather than a copy: a
 * query string the client writes and one the API reads must mean the same
 * thing.
 *
 * Filters: category, proximity (H11), min price (H12), rating (H5), plus a
 * name/category keyword, with suggestions as it is typed (H56). A result is a
 * Business (H20).
 */
interface SearchPayload {
  results: BusinessCard[];
  favorites: string[];
}

/** Suggestions for the typed text (H56). A refusal is an empty list. */
async function suggest(typed: string): Promise<Suggestion[]> {
  const result = await call<{ suggestions: Suggestion[] }>(
    `/suggestions?q=${encodeURIComponent(typed)}`,
    { anonymous: true },
  );
  return result.ok ? result.value.suggestions : [];
}

export function SearchScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { me } = useMe();

  const search = params.toString();
  const current = parseSearchParams(Object.fromEntries(params.entries()));
  const signedIn = me?.kind === "consumer";
  // The search the visitor is looking at, so a guest who taps a heart comes
  // back to exactly these results rather than to a bare `/search`.
  const here = search ? `/search?${search}` : "/search";

  // The filter chips outlive any one search, so they are their own read: a
  // keyword change must not empty the category row while the results reload.
  const categories = useQuery<{ categories: { id: string; name: string }[] }>(
    "/categories",
    { anonymous: true },
  );

  // Re-reads on every query-string change, which is every filter, chip and
  // keyword: they all navigate rather than setting state, so the URL stays the
  // one thing that says what is on screen.
  const results = useQuery<SearchPayload>(`/search?${search}`);
  const payload = results.data;

  const favorites = new Set(payload?.favorites ?? []);

  return (
    <PublicPage>
      <div className="relative overflow-hidden border-b border-line">
        <div className="mesh opacity-60" aria-hidden />
        <div className="relative mx-auto flex w-full max-w-6xl items-start justify-between gap-4 px-5 pb-8 pt-10">
          <div>
            <h1 className="text-3xl font-extrabold text-ink sm:text-4xl">
              Find care
            </h1>
            <p className="mt-2 text-body">
              Search businesses near you, then request an appointment.
            </p>
          </div>
          {/* The saved clinics, top right (2 November 2026). A guest has
              nothing saved, so sees none (H35). */}
          {signedIn && <FavoritesLink />}
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-5 py-8">
        <SearchControls
          categories={categories.data?.categories ?? []}
          current={{
            categoryId: current.categoryId,
            keyword: current.keyword,
            maxPriceCents: current.maxPriceCents,
            minRating: current.minRating,
            point: current.point,
            radiusM: current.radiusM,
          }}
          push={(url) => void navigate(url)}
          suggest={suggest}
        />

        {results.loading && !payload ? (
          <ScreenPending />
        ) : results.failure ? (
          // The client cannot inspect the server's environment, so the two
          // halves of this state are told apart by the **code** the API sent:
          // `not_configured` is a build nobody pointed at a database, anything
          // else is a real failure. Same distinction, told from the far side.
          <DataUnavailable
            configured={results.failure !== "not_configured"}
            what="search results"
          />
        ) : payload && payload.results.length === 0 ? (
          <div className="rounded-2xl border border-line bg-surface px-5 py-16 text-center">
            <span className="mx-auto flex size-12 items-center justify-center rounded-2xl bg-accent/12 text-accent">
              <SearchX className="size-6" aria-hidden />
            </span>
            <p className="mt-4 font-display text-lg font-bold text-ink">
              No businesses match your filters.
            </p>
            <p className="mx-auto mt-1 max-w-sm text-sm text-muted">
              Try widening the distance, raising the price, or clearing a filter.
            </p>
          </div>
        ) : payload ? (
          <>
            <p className="mb-4 text-sm text-muted">
              <span className="font-mono font-semibold text-ink">
                {payload.results.length}
              </span>{" "}
              {payload.results.length === 1 ? "business" : "businesses"}
            </p>
            <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {payload.results.map((card) => (
                <li key={card.id}>
                  <BusinessCardTile
                    card={card}
                    favorite={favorites.has(card.id)}
                    signedIn={signedIn}
                    next={here}
                  />
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </div>
    </PublicPage>
  );
}
